import { KeyClubProfile as KeyClubProfileType } from '@/lib/types/keyClubProfile';

interface KeyClubErrorResponse {
  error: string;
  message?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeKeyClubPayload(data: Record<string, any>): Record<string, any> {
  const cleaned = { ...data };

  delete cleaned.purchase_history;

  if (cleaned.dob === '--') {
    delete cleaned.dob;
  }

  if (cleaned.preferences) {
    const prefs = cleaned.preferences;
    const allZero = Object.values(prefs).every((v) => v === '0');
    const hasStoreZero = Object.prototype.hasOwnProperty.call(prefs, 'store') && prefs.store === '0';
    if (allZero && !hasStoreZero) {
      delete cleaned.preferences;
    }
  }

  return cleaned;
}

function parseKeyClubError(text: string): KeyClubErrorResponse {
  if (!text.trim()) {
    return { error: 'Failed to update Key Club profile.' };
  }

  try {
    return JSON.parse(text) as KeyClubErrorResponse;
  } catch {
    return { error: text };
  }
}

/**
 * Updates the user's Key Club profile.
 *
 * @param {string} keyClubNumber - The user's Key Club number.
 * @param {Partial<KeyClubProfileType>} updatedData - The updated profile data.
 * @returns {Promise<KeyClubProfileType>} - The updated Key Club profile data.
 */
export async function updateUserKeyClubProfile(
  keyClubNumber: string,
  updatedData: Partial<KeyClubProfileType>
): Promise<KeyClubProfileType> {
  if (!keyClubNumber) {
    throw new Error('Key Club number is required.');
  }

  try {
    const safeUpdatedData = sanitizeKeyClubPayload(updatedData);
    const response = await fetch(`/api/keyclub/member/${keyClubNumber}/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Pragma': 'no-cache',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Expires': '0',
      },
      body: JSON.stringify(safeUpdatedData),
    });

    const text = await response.text();

    if (!response.ok) {
      const errorData = parseKeyClubError(text);
      console.error('Failed to update Key Club profile:', errorData);
      throw new Error(errorData.message || errorData.error || 'Failed to update Key Club profile.');
    }

    if (!text.trim()) {
      return updatedData as KeyClubProfileType;
    }

    return JSON.parse(text) as KeyClubProfileType;
  } catch (error) {
    console.error('Error updating Key Club profile:', error);
    throw error;
  }
}
